import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import {
  groupTenantsByUnit,
  TenantGrouping,
  TenantListSortOption,
} from '../../utils/TenantUtils';
import TenantListItem from './TenantListItem';
import MergedTenantListItem from './MergedTenantListItem';
import { TenantWithFloor } from '../../types/unit';
import { cn } from '../../utils/classNames';

interface TenantListProps {
  tenantGroups: TenantGrouping[];
  /** Scrolls to TenantGrouping in tenantGroups list based on value */
  scrollTo?: string;
  sort?: TenantListSortOption;
  onListItemClick?: (tenant: TenantWithFloor) => void;
  addRightPadding?: boolean;
  columns?: 1 | 2;
}

const TenantList = forwardRef<HTMLDivElement, TenantListProps>(
  (
    {
      tenantGroups = [],
      sort,
      scrollTo,
      onListItemClick,
      addRightPadding,
      columns = 2,
    },
    ref,
  ) => {
    const groupRefs = useRef<Record<string, HTMLDivElement>>({});

    useEffect(() => {
      if (!scrollTo || !groupRefs.current[scrollTo]) {
        return;
      }

      groupRefs.current[scrollTo].scrollIntoView({ behavior: 'smooth' });
    }, [scrollTo]);

    return (
      <div
        className={`ml-12 flex grow flex-col gap-8 overflow-auto pb-8 ${
          addRightPadding ? 'pr-12' : ''
        }`}
        ref={ref}
        id="tenantList"
      >
        {tenantGroups.map((tGroup) => (
          <section
            className="flex flex-col items-start gap-4"
            key={tGroup.group}
            ref={(el: HTMLDivElement) => {
              groupRefs.current[tGroup.group] = el;
            }}
          >
            <h2 className="pt-2 text-center text-[36px]">{tGroup.group}</h2>
            <ul
              className={cn(
                'mr-2 grid w-full auto-rows-max gap-4 overflow-hidden',
                columns === 1 ? 'grid-cols-1' : 'grid-cols-2',
              )}
            >
              {columns === 1 ? (
                <MergedTenantGroup
                  tenants={tGroup.tenants}
                  sort={sort}
                  onListItemClick={onListItemClick}
                />
              ) : (
                tGroup.tenants.map((tenant) => (
                  <TenantListItem
                    key={tenant.id}
                    tenant={tenant}
                    sort={sort}
                    onClick={onListItemClick}
                  />
                ))
              )}
            </ul>
          </section>
        ))}
      </div>
    );
  },
);

interface MergedTenantGroupProps {
  tenants: TenantWithFloor[];
  sort?: TenantListSortOption;
  onListItemClick?: (tenant: TenantWithFloor) => void;
}

const MergedTenantGroup: React.FC<MergedTenantGroupProps> = ({
  tenants,
  sort,
  onListItemClick,
}) => {
  const unitGroups = useMemo(() => groupTenantsByUnit(tenants), [tenants]);

  return (
    <>
      {unitGroups.map((group) => (
        <MergedTenantListItem
          key={group.map((t) => t.id).join('-')}
          tenants={group}
          sort={sort}
          onClick={onListItemClick}
        />
      ))}
    </>
  );
};

export default TenantList;
